import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  Lead, 
  Contact, 
  BusinessCategory, 
  EmailCampaign, 
  EmailSend, 
  User,
  SearchFilters,
  PaginationParams,
  SearchResults,
  DashboardStats,
  LocationSearchRequest,
  CategorySearchRequest,
  BulkSearchRequest,
  SearchResponse,
  LeadFormData,
  ContactFormData,
  CampaignFormData
} from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: '/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token (disabled for demo)
    this.api.interceptors.request.use((config) => {
      // Skip auth for demo
      // const token = localStorage.getItem('auth_token');
      // if (token) {
      //   config.headers.Authorization = `Bearer ${token}`;
      // }
      return config;
    });

    // Response interceptor for error handling (disabled for demo)
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // Disabled auth redirect for demo
        // if (error.response?.status === 401) {
        //   localStorage.removeItem('auth_token');
        //   window.location.href = '/login';
        // }
        return Promise.reject(error);
      }
    );
  }

  // Authentication
  async login(email: string, password: string): Promise<{ token: string; user: User }> {
    const response = await this.api.post('/auth/login', { email, password });
    return response.data;
  }

  async register(email: string, password: string, first_name?: string, last_name?: string): Promise<{ token: string; user: User }> {
    const response = await this.api.post('/auth/register', { 
      email, 
      password, 
      first_name, 
      last_name 
    });
    return response.data;
  }

  async getProfile(): Promise<User> {
    const response = await this.api.get('/auth/profile');
    return response.data;
  }

  async updateProfile(data: { first_name?: string; last_name?: string }): Promise<User> {
    const response = await this.api.put('/auth/profile', data);
    return response.data;
  }

  async changePassword(current_password: string, new_password: string): Promise<void> {
    await this.api.post('/auth/change-password', { current_password, new_password });
  }

  // Leads
  async getLeads(filters?: SearchFilters, pagination?: PaginationParams): Promise<SearchResults> {
    const params = { ...filters, ...pagination };
    const response = await this.api.get('/leads', { params });
    return response.data;
  }

  async getLead(id: string): Promise<Lead> {
    const response = await this.api.get(`/leads/${id}`);
    return response.data;
  }

  async updateLead(id: string, data: Partial<LeadFormData>): Promise<Lead> {
    const response = await this.api.put(`/leads/${id}`, data);
    return response.data;
  }

  async deleteLead(id: string): Promise<void> {
    await this.api.delete(`/leads/${id}`);
  }

  async addContactToLead(leadId: string, contactData: ContactFormData): Promise<Contact> {
    const response = await this.api.post(`/leads/${leadId}/contacts`, contactData);
    return response.data;
  }

  async updateLeadScore(id: string): Promise<{ lead_score: number }> {
    const response = await this.api.put(`/leads/${id}/score`);
    return response.data;
  }

  async markLeadAsContacted(id: string): Promise<void> {
    await this.api.post(`/leads/${id}/contact`);
  }

  async exportLead(id: string): Promise<Blob> {
    const response = await this.api.get(`/leads/${id}/export`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async exportLeadsToExcel(filters?: SearchFilters): Promise<Blob> {
    const response = await this.api.get('/leads/export/excel', {
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  }

  async exportSelectedLeadsToExcel(leadIds: string[], filters?: SearchFilters): Promise<Blob> {
    const response = await this.api.post('/leads/export/excel', {
      lead_ids: leadIds,
      filters: filters
    }, {
      responseType: 'blob'
    });
    return response.data;
  }

  async discoverEmail(id: string): Promise<any> {
    const response = await this.api.post(`/leads/${id}/discover-email`);
    return response.data;
  }

  async getLeadsStats(filters?: SearchFilters): Promise<any> {
    const response = await this.api.get('/leads/stats', { params: filters });
    return response.data;
  }

  // Search
  async searchByLocation(request: LocationSearchRequest): Promise<SearchResponse> {
    const response = await this.api.post('/search/location', request);
    return response.data;
  }

  async searchByCategory(request: CategorySearchRequest): Promise<SearchResponse> {
    const response = await this.api.post('/search/category', request);
    return response.data;
  }

  async searchByKeyword(request: { keyword: string; location: string; save_results?: boolean }): Promise<SearchResponse> {
    const response = await this.api.post('/search/keyword', request);
    return response.data;
  }

  async bulkSearch(request: BulkSearchRequest): Promise<any> {
    const response = await this.api.post('/search/bulk', request);
    return response.data;
  }

  async allStatesSearch(request: { categories: string[]; priority_states?: string[]; max_per_state?: number }): Promise<any> {
    const response = await this.api.post('/search/all-states', request);
    return response.data;
  }

  async getCategories(): Promise<BusinessCategory[]> {
    const response = await this.api.get('/search/categories');
    return response.data;
  }

  async getSearchStatus(searchId: string): Promise<any> {
    const response = await this.api.get(`/search/status/${searchId}`);
    return response.data;
  }

  // Dashboard
  async getDashboardOverview(): Promise<DashboardStats> {
    const response = await this.api.get('/dashboard/overview');
    return response.data;
  }

  async getLeadsByDate(days: number = 30): Promise<any[]> {
    const response = await this.api.get('/dashboard/charts/leads-by-date', {
      params: { days }
    });
    return response.data;
  }

  async getLeadsByState(): Promise<any[]> {
    const response = await this.api.get('/dashboard/charts/leads-by-state');
    return response.data;
  }

  async getScoreDistribution(): Promise<any[]> {
    const response = await this.api.get('/dashboard/charts/score-distribution');
    return response.data;
  }

  async getCategoryPerformance(): Promise<any[]> {
    const response = await this.api.get('/dashboard/charts/category-performance');
    return response.data;
  }

  async getTopLeads(limit: number = 20): Promise<Lead[]> {
    const response = await this.api.get('/dashboard/top-leads', {
      params: { limit }
    });
    return response.data;
  }

  async getSearchActivity(): Promise<any[]> {
    const response = await this.api.get('/dashboard/search-activity');
    return response.data;
  }

  // Email
  async getCampaigns(): Promise<EmailCampaign[]> {
    const response = await this.api.get('/email/campaigns');
    return response.data;
  }

  async createCampaign(data: CampaignFormData): Promise<EmailCampaign> {
    const response = await this.api.post('/email/campaigns', data);
    return response.data;
  }

  async sendEmail(leadId: string, campaignId?: string, customSubject?: string, customBody?: string): Promise<void> {
    await this.api.post('/email/send', {
      lead_id: leadId,
      campaign_id: campaignId,
      custom_subject: customSubject,
      custom_body: customBody
    });
  }

  async sendBulkEmails(leadIds: string[], campaignId: string): Promise<void> {
    await this.api.post('/email/bulk-send', {
      lead_ids: leadIds,
      campaign_id: campaignId
    });
  }

  async getEmailSends(filters?: any): Promise<EmailSend[]> {
    const response = await this.api.get('/email/sends', { params: filters });
    return response.data;
  }

  async getEmailStats(): Promise<any> {
    const response = await this.api.get('/email/stats');
    return response.data;
  }

  // Enrichment
  async enrichWebsite(leadId: string): Promise<void> {
    await this.api.post(`/enrichment/website/${leadId}`);
  }

  async enrichSocialMedia(leadId: string): Promise<void> {
    await this.api.post(`/enrichment/social/${leadId}`);
  }

  async bulkEnrichment(leadIds: string[], enrichmentTypes: string[]): Promise<void> {
    await this.api.post('/enrichment/bulk', {
      lead_ids: leadIds,
      enrichment_types: enrichmentTypes
    });
  }

  // Health check
  async healthCheck(): Promise<any> {
    const response = await this.api.get('/health');
    return response.data;
  }

  // Helper methods
  setAuthToken(token: string): void {
    localStorage.setItem('auth_token', token);
  }

  removeAuthToken(): void {
    localStorage.removeItem('auth_token');
  }

  getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }
}

export default new ApiService();